import type { ScoutToolPart } from "../message/scoutChatMessage";

import { unwrapScoutToolPayload } from "../unwrapScoutToolPayload";

export const SKILL_ACTIVATE_TOOL_NAME = "skill_activate";

type ScoutSkillActivation = {
  toolCallId: string;
  /** The exact skill, when the call or its result names it by id. */
  id?: string;
  /** Absent only while a call that named the skill by id is still running. */
  slug?: string;
  /** Display name from the tool result; absent while the call is still running. */
  name?: string;
  status: "running" | "loaded" | "failed";
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readString(value: unknown, key: string): string | undefined {
  if (!isRecord(value)) return undefined;
  const field = value[key];
  return typeof field === "string" && field.trim() ? field.trim() : undefined;
}

const SKILL_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** `skillId` holds a skill's id or, in a typed `/slug` call or an older result, its slug. */
function skillRef(skillId: string | undefined): { id?: string; slug?: string } {
  if (!skillId) return {};
  return SKILL_ID_PATTERN.test(skillId) ? { id: skillId } : { slug: skillId };
}

export function isSkillActivateTool(tool: Pick<ScoutToolPart, "toolName">): boolean {
  return tool.toolName === SKILL_ACTIVATE_TOOL_NAME;
}

/** Read one `skill_activate` call. Null for other tools or a call with no skill id yet. */
export function readSkillActivation(tool: ScoutToolPart): ScoutSkillActivation | null {
  if (!isSkillActivateTool(tool)) return null;
  const input = unwrapScoutToolPayload(tool.toolInput);
  const output = unwrapScoutToolPayload(tool.toolOutput);
  const outputSlug = readString(output, "slug");
  const ref = outputSlug
    ? { id: readString(output, "skillId"), slug: outputSlug }
    : skillRef(readString(output, "skillId") ?? readString(input, "skillId"));
  if (!ref.id && !ref.slug) return null;

  let status: ScoutSkillActivation["status"] = "loaded";
  if (tool.toolState === "input_streaming" || tool.toolState === "input_available") {
    status = "running";
  } else if (tool.toolState === "output_error" || (isRecord(output) && output.success === false)) {
    status = "failed";
  }

  return { toolCallId: tool.toolCallId, ...ref, name: readString(output, "name"), status };
}
